import { useState } from 'react'
import { Trash2, X } from 'lucide-react'

const defaultFormData = {
  nome: '',
  tipo: 'A_VISTA',
  dias_prazo: '',
  primeira_parcela_tipo: 'A_VISTA',
  primeira_parcela_dias: '',
  grupos_parcelas: [{ quantidade: 1, peso: 1, dias: 30 }]
}

export default function NovoMeioPagamentoModal({ isOpen, onClose, onSave }) {
  const [formData, setFormData] = useState(defaultFormData)
  const [saving, setSaving] = useState(false)
  const [erro, setErro] = useState('')

  const resetForm = () => setFormData(defaultFormData)

  const handleClose = () => {
    resetForm()
    setErro('')
    onClose()
  }

  const addGrupoParcela = () => {
    setFormData(prev => ({
      ...prev,
      grupos_parcelas: [...prev.grupos_parcelas, { quantidade: 1, peso: 1, dias: 30 }]
    }))
  }

  const removeGrupoParcela = (index) => {
    setFormData(prev => ({
      ...prev,
      grupos_parcelas: prev.grupos_parcelas.filter((_, i) => i !== index)
    }))
  }

  const updateGrupoParcela = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      grupos_parcelas: prev.grupos_parcelas.map((g, i) =>
        i === index ? { ...g, [field]: parseInt(value) || 0 } : g
      )
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setErro('')

    if (!formData.nome.trim()) {
      setErro('Nome é obrigatório')
      return
    }
    if (formData.tipo === 'PARCELADO' && !formData.grupos_parcelas?.length) {
      setErro('Adicione pelo menos um grupo de parcelas')
      return
    }

    const dados = {
      nome: formData.nome.trim(),
      tipo: formData.tipo,
      dias_prazo: formData.tipo === 'PRAZO' ? parseInt(formData.dias_prazo) || null : null,
      primeira_parcela_tipo: formData.tipo === 'PARCELADO' ? formData.primeira_parcela_tipo : null,
      primeira_parcela_dias: formData.tipo === 'PARCELADO' && formData.primeira_parcela_tipo === 'DIAS'
        ? parseInt(formData.primeira_parcela_dias) || null
        : null,
      // Compat: mantém o intervalo do 1º grupo aqui pra telas antigas que só
      // leem esse campo (a fonte da verdade agora é o "dias" de cada grupo).
      intervalo_dias: formData.tipo === 'PARCELADO' ? (formData.grupos_parcelas[0]?.dias || 30) : null,
      grupos_parcelas: formData.tipo === 'PARCELADO' ? formData.grupos_parcelas : null
    }

    try {
      setSaving(true)
      await onSave(dados)
      resetForm()
    } catch (err) {
      console.error('Erro ao salvar meio de pagamento:', err)
      setErro('Erro ao salvar meio de pagamento')
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">Novo Meio de Pagamento</h2>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {erro && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">
              {erro}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
            <input
              type="text"
              value={formData.nome}
              onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
              className="input-rematei"
              placeholder="Ex: 3x iguais, À vista, 30/60/90"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo *</label>
            <select
              value={formData.tipo}
              onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
              className="input-rematei"
            >
              <option value="A_VISTA">À Vista</option>
              <option value="PRAZO">A Prazo</option>
              <option value="PARCELADO">Parcelado</option>
            </select>
          </div>

          {formData.tipo === 'PRAZO' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Dias de Prazo</label>
              <input
                type="number"
                min="1"
                value={formData.dias_prazo}
                onChange={(e) => setFormData({ ...formData, dias_prazo: e.target.value })}
                className="input-rematei"
                placeholder="Ex: 30"
              />
            </div>
          )}

          {formData.tipo === 'PARCELADO' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vencimento da 1ª Parcela</label>
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, primeira_parcela_tipo: 'A_VISTA' })}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      formData.primeira_parcela_tipo === 'A_VISTA'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    À Vista
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, primeira_parcela_tipo: 'DIAS' })}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      formData.primeira_parcela_tipo === 'DIAS'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    A Prazo
                  </button>
                </div>
              </div>

              {formData.primeira_parcela_tipo === 'DIAS' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Dias para 1ª Parcela</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.primeira_parcela_dias}
                    onChange={(e) => setFormData({ ...formData, primeira_parcela_dias: e.target.value })}
                    className="input-rematei"
                    placeholder="Ex: 5"
                  />
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Grupos de Parcelas</label>
                    <p className="text-xs text-gray-400">Cada grupo já vem com 30 dias de intervalo — edite se precisar</p>
                  </div>
                  <button type="button" onClick={addGrupoParcela} className="text-xs text-blue-600 hover:text-blue-800">
                    + Grupo
                  </button>
                </div>
                <div className="space-y-2">
                  {formData.grupos_parcelas.map((grupo, index) => {
                    const isUltimoGrupo = index === formData.grupos_parcelas.length - 1
                    const temProximaParcela = grupo.quantidade > 1 || !isUltimoGrupo
                    return (
                    <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm text-gray-500 w-6">{index + 1}</span>
                      <div className="flex-1">
                        <label className="text-xs text-gray-500">Qtd. parcelas</label>
                        <input
                          type="number"
                          min="1"
                          value={grupo.quantidade}
                          onChange={(e) => updateGrupoParcela(index, 'quantidade', e.target.value)}
                          className="input-rematei text-sm"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="text-xs text-gray-500">Peso</label>
                        <input
                          type="number"
                          min="1"
                          value={grupo.peso}
                          onChange={(e) => updateGrupoParcela(index, 'peso', e.target.value)}
                          className="input-rematei text-sm"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="text-xs text-gray-500">Dias p/ próxima</label>
                        <input
                          type="number"
                          min="1"
                          disabled={!temProximaParcela}
                          value={temProximaParcela ? (grupo.dias ?? 30) : ''}
                          placeholder={temProximaParcela ? '30' : '—'}
                          onChange={(e) => updateGrupoParcela(index, 'dias', e.target.value)}
                          className={`input-rematei text-sm ${!temProximaParcela ? 'bg-gray-100 text-gray-300 cursor-not-allowed' : ''}`}
                        />
                      </div>
                      {formData.grupos_parcelas.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeGrupoParcela(index)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    )
                  })}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Total: {formData.grupos_parcelas.reduce((acc, g) => acc + (g.quantidade || 0), 0)} parcelas
                </p>
              </div>
            </>
          )}

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={handleClose} className="btn-secondary">
              Cancelar
            </button>
            <button type="submit" disabled={saving} className="btn-primary">
              {saving ? 'Salvando...' : 'Criar Meio de Pagamento'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
